import { Feature, FeatureCollection, Point } from "geojson";
import { GeoJSONSource } from "mapbox-gl";
import {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Layer,
  Map,
  MapLayerMouseEvent,
  MapRef,
  NavigationControl,
  Popup,
  ScaleControl,
  Source,
} from "react-map-gl";
import { Article, useDataSource, valueArray } from "../../DataSource";
import { FALLBACK_MAP_VIEWSTATE } from "../../config";
import { Coordinates } from "../../core/types";
import MarkerMenu from "./MarkerMenu";
import ResetViewstateControl from "./ResetViewportControl";
import {
  ALL_POINTS_LAYER_ID,
  BOUNDS_PADDING,
  CLICK_TOLERANCE_PX,
  CLUSTER_COUNT_LAYER,
  CLUSTER_RADIUS,
  MAX_EXPANSION_ZOOM,
  MIN_ZOOM_CLICK,
  POINT_LAYER,
  POPUP_OFFSET,
  RENDERED_LAYER_ID,
  SOURCE_ID,
} from "./constants";
import { coordinatesToBounds, pointRadius, sortClosest } from "./helpers";
import { Badge, Flex } from "@chakra-ui/react";
import { isNumber } from "../../core/utils";

interface ArticleMapProps extends CSSProperties {}

interface MapPoint {
  feature: Feature<Point>;
  expandable?: boolean;
}

export default function ArticleMap(props: ArticleMapProps): ReactElement {
  const { ...style } = props;

  const {
    mapboxToken,
    articles,
    allArticles,
    focused,
    mapHighlight,
    setFocused,
    setTempArticle,
  } = useDataSource();

  const articleLookup = useMemo(
    (): Record<string, Article> =>
      articles.reduce(
        (acc, article) => ({ ...acc, [article.id]: article }),
        {}
      ),
    [articles]
  );

  const initialBounds = useMemo(
    () => coordinatesToBounds(allArticles.map(({ position }) => position)),
    [allArticles]
  );

  const [menuCluster, setMenuCluster] = useState<Feature<Point> | null>(null);
  const [hoverFeature, setHoverFeature] = useState<MapPoint | null>(null);

  const [menuClusterArticles, setMenuClusterArticles] = useState<
    Array<Article>
  >([]);

  const relevantArticles = useMemo(
    () =>
      mapHighlight != null
        ? articles.filter((article) =>
            mapHighlight?.every(({ column, value }) =>
              valueArray(article.data[column]).includes(value)
            )
              ? article.id
              : null
          )
        : articles,
    [articles, mapHighlight]
  );

  const articleData = useMemo(
    (): FeatureCollection => ({
      type: "FeatureCollection",
      features: relevantArticles.map(
        (article): Feature<Point> => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: article.position },
          properties: {
            id: article.id,
            shortDescription: article.shortDescription,
          },
        })
      ),
    }),
    [relevantArticles]
  );

  const ref = useRef<MapRef>(null);

  const expandCluster = useCallback(
    (cluster: Feature<Point>) => {
      if (ref.current != null) {
        (
          ref.current.getSource(SOURCE_ID) as GeoJSONSource
        ).getClusterExpansionZoom(
          cluster.properties?.cluster_id,
          (err, zoom) => {
            if (err != null) {
              throw new Error(err);
            } else if (zoom < MAX_EXPANSION_ZOOM) {
              ref.current?.flyTo({
                zoom: Math.max(zoom, MIN_ZOOM_CLICK),
                center: cluster.geometry.coordinates as Coordinates,
              });
            } else if (ref.current != null) {
              const { x, y } = ref.current.project(
                cluster.geometry.coordinates as Coordinates
              );

              const clusterFeatures = ref.current.queryRenderedFeatures(
                pointRadius(x, y, CLUSTER_RADIUS),
                { layers: [ALL_POINTS_LAYER_ID] }
              ) as Feature<Point>[];

              setMenuClusterArticles(
                clusterFeatures.reduce(
                  (acc: Array<Article>, feature) =>
                    feature.properties?.id != null &&
                    articleLookup[feature.properties.id] != null
                      ? [...acc, articleLookup[feature.properties.id]]
                      : acc,
                  []
                )
              );

              setMenuCluster(cluster);
            }
          }
        );
      }
    },
    [articleLookup]
  );

  const handleClick = useCallback(
    (evt: MapLayerMouseEvent) => {
      if (ref.current?.getLayer(RENDERED_LAYER_ID) != null) {
        const { x, y } = evt.point;

        const selectedFeatures = ref.current.queryRenderedFeatures(
          pointRadius(x, y, CLICK_TOLERANCE_PX),
          { layers: [RENDERED_LAYER_ID] }
        ) as Feature<Point>[];
        const closest = selectedFeatures.sort(sortClosest(x, y))[0];

        if (
          closest?.properties?.cluster_id !==
          menuCluster?.properties?.cluster_id
        ) {
          setMenuCluster(null);
          setMenuClusterArticles([]);
        }

        const selected =
          closest?.properties?.id != null
            ? articleLookup[closest.properties.id]
            : null;

        if (closest?.properties?.cluster) {
          expandCluster(closest);
        } else {
          setFocused(selected);
          setTempArticle(selected);
        }

        if (closest != null) {
          ref.current.flyTo({
            center: closest.geometry.coordinates as Coordinates,
          });
        }
      }
    },
    [
      articleLookup,
      expandCluster,
      menuCluster?.properties?.cluster_id,
      setFocused,
      setTempArticle,
    ]
  );

  const handleHover = useCallback(
    (evt: MapLayerMouseEvent) => {
      if (ref.current?.getLayer(RENDERED_LAYER_ID) != null) {
        const { x, y } = evt.point;

        const selectedFeatures = ref.current.queryRenderedFeatures(
          pointRadius(x, y, CLICK_TOLERANCE_PX),
          { layers: [RENDERED_LAYER_ID] }
        ) as Feature<Point>[];

        const closest = selectedFeatures.sort(sortClosest(x, y))[0] ?? null;
        if (closest?.properties?.cluster) {
          (
            ref.current.getSource(SOURCE_ID) as GeoJSONSource
          ).getClusterExpansionZoom(
            closest.properties?.cluster_id,
            (_err, zoom) => {
              if (ref.current != null) {
                setHoverFeature({
                  feature: closest,
                  expandable:
                    closest.properties?.cluster &&
                    zoom < ref.current.getMaxZoom(),
                });
              }
            }
          );
          setTempArticle(focused);
        } else {
          setHoverFeature({ feature: closest });
          setTempArticle(
            closest?.properties?.id != null
              ? articleLookup[closest.properties.id]
              : focused
          );
        }
      }
    },
    [articleLookup, focused, setTempArticle]
  );

  const [focusedClusterId, setFocusedClusterId] = useState<number | null>(null);
  useEffect(() => {
    // Forcing the dependencies to include this, so on re-renders it updates
    articleData!;
    if (ref.current?.loaded && focused != null) {
      const clusters = ref.current?.queryRenderedFeatures(undefined, {
        layers: [CLUSTER_COUNT_LAYER.id],
      });
      const source = ref.current?.getSource(SOURCE_ID);
      if (source.type === "geojson" && clusters != null) {
        for (const cluster of clusters) {
          const clusterId = isNumber(cluster.id) ? cluster.id : null;
          if (clusterId != null) {
            source.getClusterLeaves(clusterId, 1000, 0, (_err, features) =>
              features?.forEach((feature) =>
                feature.properties?.id === focused?.id
                  ? setFocusedClusterId(clusterId)
                  : null
              )
            );
          }
        }
      }
    } else {
      setFocusedClusterId(null);
    }
  }, [articleData, focused]);

  const pointLayer = useMemo(
    () =>
      POINT_LAYER(
        focusedClusterId ?? focused?.id ?? null,
        focusedClusterId != null
      ),
    [focusedClusterId, focused?.id]
  );

  return (
    <Map
      ref={ref}
      interactiveLayerIds={[SOURCE_ID]}
      mapboxAccessToken={mapboxToken}
      initialViewState={
        initialBounds != null
          ? {
              bounds: initialBounds,
              fitBoundsOptions: { padding: BOUNDS_PADDING },
            }
          : FALLBACK_MAP_VIEWSTATE
      }
      style={style}
      mapStyle="mapbox://styles/ncolemanclarke/cllar0f4m00qt01ph19oi90tt"
      projection={{ name: "mercator" }}
      onClick={handleClick}
      onMouseMove={handleHover}
    >
      <Flex position="absolute" top="2" w="100%" justify="center">
        <Badge
          fontSize="sm"
          rounded="md"
          variant="outline"
          textTransform="none"
          bg="white"
          opacity={0.8}
        >
          Showing {articles.length} / {allArticles.length}
        </Badge>
      </Flex>
      <NavigationControl />
      <ResetViewstateControl
        position="top-right"
        onClick={() =>
          ref.current == null
            ? null
            : initialBounds != null
            ? ref.current.fitBounds(initialBounds, { padding: BOUNDS_PADDING })
            : ref.current.flyTo(FALLBACK_MAP_VIEWSTATE)
        }
      />
      <ScaleControl position="bottom-right" />
      <Source id="all-points" type="geojson" data={articleData}>
        <Layer
          id={ALL_POINTS_LAYER_ID}
          type="circle"
          paint={{ "circle-opacity": 0, "circle-stroke-opacity": 0 }}
        />
      </Source>
      <Source
        id={SOURCE_ID}
        type="geojson"
        data={articleData}
        cluster={true}
        clusterRadius={CLUSTER_RADIUS}
        clusterMaxZoom={24}
      >
        <Layer {...pointLayer} />
        <Layer {...CLUSTER_COUNT_LAYER} />
      </Source>
      {hoverFeature?.feature != null && (
        <Popup
          closeButton={false}
          closeOnClick={true}
          focusAfterOpen={false}
          offset={POPUP_OFFSET}
          longitude={hoverFeature.feature.geometry.coordinates[0]}
          latitude={hoverFeature.feature.geometry.coordinates[1]}
        >
          {(hoverFeature.feature.properties?.shortDescription?.length ?? 0) > 0
            ? hoverFeature.feature.properties?.shortDescription
            : !hoverFeature.feature.properties?.cluster
            ? "Description not found"
            : hoverFeature.expandable
            ? "Zoom in"
            : "Show more"}
        </Popup>
      )}
      {menuCluster != null && (
        <MarkerMenu
          longitude={menuCluster.geometry.coordinates[0]}
          latitude={menuCluster.geometry.coordinates[1]}
          articles={menuClusterArticles}
          fallbackShortDescription="Description not found"
          focusedId={focused?.id ?? null}
          onArticleClick={(article) => {
            setTempArticle(article);
            setFocused(article);
          }}
          onArticleHover={setTempArticle}
          onClose={() => {
            setMenuCluster(null);
            setMenuClusterArticles([]);
          }}
        />
      )}
    </Map>
  );
}
